/* ==========================================================================
 * Dodo Photo Upload — Cloudinary-backed uploader (vanilla JS, no jQuery).
 *
 * Replaces LC File Upload on the Custom Portraits PDP.
 *
 * Contract:
 *  - On each successful upload, writes <input type="hidden"
 *      name="properties[Photo N]" value="<secure_url>"> inside
 *      form.product-single__form (FormData siblings of other props).
 *  - Maintains <input name="properties[_uploaded_photos_json]" value='[...]'>
 *      as a sentinel array of all URLs.
 *  - Disables [data-add-to-cart] + [data-sticky-atc-btn] (with aria-busy) while
 *      uploads are pending OR until min count is met. Only re-enables buttons
 *      it disabled itself (tracked via data-dpu-disabled).
 *  - Uses XHR (not fetch) so upload.progress events drive the per-thumb bar.
 * ========================================================================== */
(function () {
  'use strict';

  /* ----- Cloudinary configuration ----------------------------------------- */
  var CLOUDINARY_CLOUD  = 'dzcwejxx0';
  var CLOUDINARY_PRESET = 'dodo_portrait_uploads';
  var CLOUDINARY_URL    = 'https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD + '/image/upload';

  var ALLOWED_TYPES = [
    'image/jpeg', 'image/jpg', 'image/png',
    'image/webp', 'image/heic', 'image/heif'
  ];
  var ALLOWED_EXT_RE = /\.(jpe?g|png|webp|heic|heif)$/i;
  var HEIC_RE        = /\.(heic|heif)$/i;

  /* ----- Helpers ---------------------------------------------------------- */
  function uid() {
    return 'p_' + Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
  }

  function findProductForm(root) {
    return root.closest('form.product-single__form');
  }

  function findATCButtons(form) {
    var out = [];
    if (form) {
      form.querySelectorAll('[data-add-to-cart]').forEach(function (b) { out.push(b); });
    }
    document.querySelectorAll('[data-sticky-atc-btn]').forEach(function (b) { out.push(b); });
    return out;
  }

  /* ----- Uploader instance ------------------------------------------------ */
  function Uploader(root) {
    this.root      = root;
    this.form      = findProductForm(root);
    this.i18n      = safeParseJSON(root.getAttribute('data-i18n')) || {};
    this.minFiles  = parseInt(root.getAttribute('data-min-files'), 10) || 1;
    this.maxFiles  = parseInt(root.getAttribute('data-max-files'), 10) || 10;
    this.maxSize   = parseInt(root.getAttribute('data-max-size'),  10) || 20971520;
    this.files     = [];

    this.dropzone     = root.querySelector('[data-dropzone]');
    this.fileInput    = root.querySelector('[data-file-input]');
    this.grid         = root.querySelector('[data-grid]');
    this.statusCount  = root.querySelector('[data-count]');
    this.statusLabel  = root.querySelector('[data-status-label]');
    this.globalError  = root.querySelector('[data-global-error]');
    this.thumbTpl     = document.querySelector('[data-dpu-thumb-template]');

    // Container for hidden inputs — kept as a sibling of the uploader inside
    // the same form so FormData picks them up at submit time.
    this.hiddenContainer = document.createElement('div');
    this.hiddenContainer.style.display = 'none';
    this.hiddenContainer.setAttribute('data-dpu-hidden', '');
    root.parentNode.insertBefore(this.hiddenContainer, root.nextSibling);

    this.bind();
    this.render();
    this.syncFormInputs();
    this.updateATCState();
  }

  Uploader.prototype.bind = function () {
    var self = this;

    this.dropzone.addEventListener('click', function (e) {
      e.preventDefault();
      self.fileInput.click();
    });

    this.dropzone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        self.fileInput.click();
      }
    });

    this.fileInput.addEventListener('change', function (e) {
      self.handleFiles(e.target.files);
      // Reset so re-selecting the same file fires change again
      self.fileInput.value = '';
    });

    // Drag/drop on dropzone
    ['dragenter', 'dragover'].forEach(function (ev) {
      self.dropzone.addEventListener(ev, function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        self.dropzone.classList.add('is-dragging');
      });
    });

    self.dropzone.addEventListener('dragleave', function (e) {
      // Only un-set when leaving the dropzone (not its children)
      if (self.dropzone.contains(e.relatedTarget)) return;
      self.dropzone.classList.remove('is-dragging');
    });

    self.dropzone.addEventListener('drop', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.dropzone.classList.remove('is-dragging');
      var dt = e.dataTransfer;
      if (dt && dt.files && dt.files.length) {
        self.handleFiles(dt.files);
      }
    });

  };

  Uploader.prototype.validate = function (file) {
    var typeOK = ALLOWED_TYPES.indexOf(file.type) !== -1 || ALLOWED_EXT_RE.test(file.name);
    if (!typeOK) return this.i18n.errType || 'Unsupported format';
    if (file.size > this.maxSize) return this.i18n.errSize || 'File too large';
    return null;
  };

  Uploader.prototype.handleFiles = function (fileList) {
    this.hideGlobalError();
    var remaining = this.maxFiles - this.files.length;
    if (remaining <= 0) {
      this.showGlobalError(this.i18n.errMax || 'Maximum reached');
      return;
    }
    var incoming = Array.prototype.slice.call(fileList, 0, remaining);
    if (fileList.length > remaining) {
      this.showGlobalError(this.i18n.errMax || 'Maximum reached');
    }
    for (var i = 0; i < incoming.length; i++) {
      this.addFile(incoming[i]);
    }
    this.render();
    this.updateATCState();
  };

  Uploader.prototype.addFile = function (file) {
    var error = this.validate(file);
    var entry = {
      id: uid(),
      file: file,
      name: file.name,
      previewUrl: null,
      status: error ? 'error' : 'queued',
      progress: 0,
      secureUrl: null,
      error: error,
      xhr: null
    };
    this.files.push(entry);
    this.generatePreview(entry);
    if (!error) this.upload(entry);
  };

  Uploader.prototype.generatePreview = function (entry) {
    // HEIC/HEIF can't be previewed by browsers — show fallback icon instead.
    if (HEIC_RE.test(entry.name) || entry.file.type === 'image/heic' || entry.file.type === 'image/heif') {
      entry.previewUrl = null;
      return;
    }
    try {
      entry.previewUrl = URL.createObjectURL(entry.file);
    } catch (e) {
      entry.previewUrl = null;
    }
  };

  Uploader.prototype.upload = function (entry) {
    var self = this;
    entry.status = 'uploading';
    entry.progress = 0;
    entry.error = null;
    this.render();
    this.updateATCState();

    var fd = new FormData();
    fd.append('file', entry.file);
    fd.append('upload_preset', CLOUDINARY_PRESET);

    var xhr = new XMLHttpRequest();
    entry.xhr = xhr;
    xhr.open('POST', CLOUDINARY_URL, true);

    xhr.upload.addEventListener('progress', function (e) {
      if (e.lengthComputable) {
        entry.progress = Math.max(1, Math.round((e.loaded / e.total) * 100));
        self.renderProgress(entry);
      }
    });

    xhr.addEventListener('load', function () {
      entry.xhr = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        var data = safeParseJSON(xhr.responseText);
        if (data && data.secure_url) {
          entry.secureUrl = data.secure_url;
          entry.status = 'success';
          entry.progress = 100;
        } else {
          entry.status = 'error';
          entry.error = self.i18n.errUpload || 'Upload failed';
        }
      } else {
        entry.status = 'error';
        entry.error = self.i18n.errUpload || 'Upload failed';
      }
      self.render();
      self.syncFormInputs();
      self.updateATCState();
    });

    xhr.addEventListener('error', function () {
      entry.xhr = null;
      entry.status = 'error';
      entry.error = self.i18n.errUpload || 'Upload failed';
      self.render();
      self.updateATCState();
    });

    xhr.addEventListener('abort', function () {
      entry.xhr = null;
    });

    xhr.send(fd);
  };

  Uploader.prototype.remove = function (id) {
    var idx = this.indexOf(id);
    if (idx === -1) return;
    var entry = this.files[idx];
    if (entry.xhr) { try { entry.xhr.abort(); } catch (e) { /* noop */ } }
    if (entry.previewUrl) { try { URL.revokeObjectURL(entry.previewUrl); } catch (e) { /* noop */ } }
    this.files.splice(idx, 1);
    this.render();
    this.syncFormInputs();
    this.updateATCState();
  };

  Uploader.prototype.retry = function (id) {
    var idx = this.indexOf(id);
    if (idx === -1) return;
    var entry = this.files[idx];
    var validationError = this.validate(entry.file);
    if (validationError) {
      entry.status = 'error';
      entry.error  = validationError;
      this.render();
      return;
    }
    this.upload(entry);
  };

  Uploader.prototype.indexOf = function (id) {
    for (var i = 0; i < this.files.length; i++) {
      if (this.files[i].id === id) return i;
    }
    return -1;
  };

  /* ----- Rendering -------------------------------------------------------- */
  Uploader.prototype.renderProgress = function (entry) {
    var node = this.grid.querySelector('[data-thumb-id="' + entry.id + '"]');
    if (!node) return;
    var bar = node.querySelector('[data-thumb-bar]');
    if (bar) bar.style.width = entry.progress + '%';
  };

  Uploader.prototype.render = function () {
    var self = this;
    this.grid.hidden = this.files.length === 0;

    var existing = {};
    Array.prototype.slice.call(this.grid.children).forEach(function (node) {
      var id = node.getAttribute('data-thumb-id');
      if (id) existing[id] = node;
    });

    var current = {};
    this.files.forEach(function (entry, index) {
      current[entry.id] = true;
      var node = existing[entry.id];
      if (!node) {
        node = self.buildThumb(entry, index);
        self.grid.appendChild(node);
      }
      self.paintThumb(node, entry);
    });

    // Remove nodes whose entries are gone
    Object.keys(existing).forEach(function (id) {
      if (!current[id]) {
        var n = existing[id];
        if (n && n.parentNode) n.parentNode.removeChild(n);
      }
    });

    this.renderStatus();
  };

  Uploader.prototype.buildThumb = function (entry, index) {
    var self = this;
    var node = this.thumbTpl.content.firstElementChild.cloneNode(true);
    node.setAttribute('data-thumb-id', entry.id);
    node.style.animationDelay = Math.min(index * 30, 240) + 'ms';

    var removeBtn = node.querySelector('[data-thumb-remove]');
    removeBtn.setAttribute('aria-label', this.i18n.remove || 'Remove');
    removeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.remove(entry.id);
    });

    var retryBtn = node.querySelector('[data-thumb-retry]');
    var retryLbl = node.querySelector('[data-thumb-retry-label]');
    if (retryLbl) retryLbl.textContent = this.i18n.retry || 'Retry';
    retryBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.retry(entry.id);
    });

    return node;
  };

  Uploader.prototype.paintThumb = function (node, entry) {
    node.setAttribute('data-status', entry.status);
    node.setAttribute('data-has-preview', entry.previewUrl ? 'true' : 'false');

    var img = node.querySelector('[data-thumb-img]');
    if (entry.previewUrl) {
      if (img.getAttribute('src') !== entry.previewUrl) {
        img.src = entry.previewUrl;
        img.alt = entry.name;
      }
    } else {
      img.removeAttribute('src');
      img.alt = '';
    }

    var nameEl = node.querySelector('[data-thumb-name]');
    if (nameEl) nameEl.textContent = entry.name;

    var errEl = node.querySelector('[data-thumb-error]');
    if (errEl) {
      if (entry.error) {
        errEl.textContent = entry.error;
        errEl.hidden = false;
      } else {
        errEl.textContent = '';
        errEl.hidden = true;
      }
    }

    var bar = node.querySelector('[data-thumb-bar]');
    if (bar) bar.style.width = entry.progress + '%';
  };

  Uploader.prototype.renderStatus = function () {
    var successful = 0;
    var uploading  = 0;
    this.files.forEach(function (e) {
      if (e.status === 'success') successful++;
      else if (e.status === 'uploading' || e.status === 'queued') uploading++;
    });

    this.statusCount.textContent = successful;

    var label;
    var state;
    if (uploading > 0) {
      label = this.i18n.uploading || 'Uploading';
      state = 'uploading';
    } else if (successful >= this.minFiles) {
      label = this.i18n.done || 'Photos uploaded';
      state = 'success';
    } else {
      label = this.i18n.required || 'At least one photo required';
      state = 'idle';
    }
    this.statusLabel.textContent = label;
    this.root.setAttribute('data-state', state);
  };

  /* ----- Form integration ------------------------------------------------- */
  Uploader.prototype.syncFormInputs = function () {
    this.hiddenContainer.innerHTML = '';
    var urls = [];
    var n = 1;
    this.files.forEach(function (entry) {
      if (entry.status === 'success' && entry.secureUrl) {
        var input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = 'properties[Photo ' + n + ']';
        input.value = entry.secureUrl;
        this.hiddenContainer.appendChild(input);
        urls.push(entry.secureUrl);
        n++;
      }
    }, this);

    var sentinel = document.createElement('input');
    sentinel.type  = 'hidden';
    sentinel.name  = 'properties[_uploaded_photos_json]';
    sentinel.value = JSON.stringify(urls);
    this.hiddenContainer.appendChild(sentinel);
  };

  Uploader.prototype.updateATCState = function () {
    var pending = 0;
    var successful = 0;
    this.files.forEach(function (e) {
      if (e.status === 'uploading' || e.status === 'queued') pending++;
      if (e.status === 'success') successful++;
    });

    var shouldDisable = pending > 0 || successful < this.minFiles;
    var buttons = findATCButtons(this.form);

    buttons.forEach(function (btn) {
      if (shouldDisable) {
        // Only claim ownership of buttons that were ENABLED when we arrived.
        // A button already disabled by variant-unavailability logic (rendered
        // disabled="disabled" in Liquid when current_variant.available is false)
        // must NOT be marked data-dpu-disabled — otherwise we'd later re-enable
        // a sold-out button when uploads complete.
        var alreadyDisabledByOther = btn.hasAttribute('disabled') && btn.getAttribute('data-dpu-disabled') !== '1';
        if (!alreadyDisabledByOther) {
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('data-dpu-disabled', '1');
        }
        if (pending > 0) {
          btn.setAttribute('aria-busy', 'true');
        } else {
          btn.removeAttribute('aria-busy');
        }
      } else if (btn.getAttribute('data-dpu-disabled') === '1') {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('data-dpu-disabled');
      }
    });
  };

  Uploader.prototype.showGlobalError = function (msg) {
    if (!this.globalError) return;
    this.globalError.textContent = msg;
    this.globalError.hidden = false;
  };
  Uploader.prototype.hideGlobalError = function () {
    if (!this.globalError) return;
    this.globalError.textContent = '';
    this.globalError.hidden = true;
  };

  /* ----- Utilities -------------------------------------------------------- */
  function safeParseJSON(str) {
    if (!str) return null;
    try { return JSON.parse(str); } catch (e) { return null; }
  }

  /* ----- Boot ------------------------------------------------------------- */
  function init() {
    document.querySelectorAll('[data-dodo-photo-upload]').forEach(function (el) {
      if (el.dataset.dpuBound === '1') return;
      el.dataset.dpuBound = '1';
      try {
        new Uploader(el);
      } catch (err) {
        console.error('[dodo-photo-upload] init failed', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the section is reloaded in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
